<template>
  <div class="w-full relative">
    <label v-if="label" :for="name" :class="['block mb-1 text-sm font-medium text-gray-700', labelClass]">
      {{ label }} <span v-if="required && !disabled" class="text-red-500">*</span>
    </label>

    <div class="relative">
      <input @wheel="type === 'number' ? $event.preventDefault() : null" @keydown="onKeyDown" :id="name" :name="name"
        :type="showPassword ? 'text' : type" v-model="internalValue" :placeholder="placeholder"
        :inputmode="type === 'number' ? 'decimal' : type === 'tel' ? 'numeric' : null"
        :pattern="type === 'number' ? '[0-9]*[.]?[0-9]*' : type == 'tel' ? '[0-9]*' : null" :class="[
          baseClass,
          error ? 'border-red-500' : 'border-gray-600',
          customClass,
          {
            '[appearance:textfield]': type === 'number',
            '[&::-webkit-outer-spin-button]:appearance-none': type === 'number',
            '[&::-webkit-inner-spin-button]:appearance-none': type === 'number'
          }
        ]" v-maska="mask" :disabled="disabled" @blur="onBlur" @input="onInput" @focus="onFocus" :maxlength="length" />

      <!-- Eye toggle icon -->
      <button v-if="type === 'password'" type="button" class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
        @click="togglePassword" tabindex="-1">
        <svg v-if="showPassword" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
          stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
          stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a9.984 9.984 0 012.398-4.368M6.343 6.343A9.964 9.964 0 0112 5c4.477 0 8.268 2.943 9.542 7a9.963 9.963 0 01-4.072 5.293M15 12a3 3 0 00-3-3m0 0a3 3 0 00-3 3m6 0a3 3 0 01-3 3m0 0a3 3 0 01-3-3m-6 6l18-18" />
        </svg>
      </button>
    </div>

    <p v-if="error && !disabled" :class="[
      'text-xs mt-1 mb-0',
      errorClass ? errorClass : 'text-[#FE5000]'
    ]" class="text-[#FE5000] text-xs mt-1 mb-0 font-roboto">{{ error }}</p>
  </div>
</template>

<script setup>
import { ref, watch, defineExpose } from 'vue';
import { vMaska } from 'maska/vue';

const props = defineProps({
  modelValue: {},
  name: String,
  label: String,
  labelClass: {
    type: String,
    default: ''
  },
  type: {
    type: String,
    default: 'text'
  },
  placeholder: String,
  required: {
    type: Boolean,
    default: false
  },
  regex: RegExp,
  errorMessage: String,
  mask: String,
  customClass: {
    type: String,
    default: ''
  },
  errorClass: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  },
  confirmWith: {
    type: String,
    default: ''
  },
  length: {
    type: String,
    default: null
  }
});

const emit = defineEmits(['update:modelValue', 'input', 'focus']); // ✅ Declare both events

const internalValue = ref(props.modelValue || '');
const error = ref('');
const touched = ref(false);
const showPassword = ref(false);

const baseClass = 'w-full mobile:p-1.5 largeDesktop:p-2 pr-10 mobile:rounded-md largeDesktop:rounded-lg border outline-none text-sm !text-[#333] disabled:cursor-not-allowed disabled:!bg-[#EFEFEF] disabled:!text-[#666] !appearance-none';

watch(() => props.modelValue, (val) => {
  internalValue.value = val || '';
});

watch(internalValue, (val) => {
  emit('update:modelValue', val);
  if (touched.value && !props.disabled) {
    validate();
  }
});

function onBlur() {
  touched.value = true;
  validate();
}

function onFocus(e) {
  emit('focus', e);
}

function onKeyDown(e) {
  // Prevent arrow keys
  if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
    e.preventDefault();
  }

  if (props.length &&
    e.target.value.length >= props.length &&
    e.key.length === 1 && // only characters, not Backspace/Delete
    !e.ctrlKey && !e.metaKey
  ) {
    e.preventDefault();
  }
}

function onInput(event) {
  let value = event.target.value;
  emit("input", value);
  if (touched.value && !props.disabled) {
    validate();
  }
}

function togglePassword() {
  showPassword.value = !showPassword.value;
}

function validate() {
  if (props.disabled) return '';
  let trimmed = ''
  let digitsOnly = ''
  if (props.type == 'number') {
    trimmed = internalValue.value || '';
  } else {
    trimmed = internalValue.value?.trim() || '';
    digitsOnly = trimmed.replace(/\D/g, '');
  }

  if (props.required && trimmed === '') {
    error.value = 'This field is required.';
    return error.value;
  }

  if (props.mask === '(###) ###-####' && (internalValue.value.length > 0 && digitsOnly.length !== 10)) {
    error.value = props.errorMessage || 'Invalid phone number.';
    return error.value;
  }

  if (trimmed !== '' && props.regex && !props.regex.test(trimmed)) {
    error.value = props.errorMessage || 'Invalid input.';
    return error.value;
  }

  if (props.confirmWith && props.confirmWith !== trimmed) {
    error.value = props.errorMessage || 'Passwords do not match.';
    return error.value;
  }

  error.value = '';
  return '';
}

function resetField() {
  touched.value = false;
  error.value = '';
  internalValue.value = '';
}

defineExpose({
  resetField,
  validate,
  hasError: () => !!error.value
});
</script>
<style scoped>
input[type="password"]::-ms-reveal,
input[type="password"]::-ms-clear {
  display: none;
}
</style>